/**
 * PlatformLayout.tsx — Platform admin shell with platform-specific navigation
 *
 * Renders a dedicated sidebar for platform admin operations: tenant management,
 * org enrollment, platform revenue, infrastructure settings.
 * Only visible to users with platform admin permissions.
 *
 * @since 2026-06-19 (SaaS conversion — Platform Admin capabilities)
 */
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import OrgSwitcher from './OrgSwitcher';

interface NavItem {
  label: string;
  to: string;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

const PLATFORM_NAV_GROUPS: NavGroup[] = [
  {
    heading: 'Platform',
    items: [
      { label: 'Dashboard', to: '/platform/dashboard' },
      { label: 'Revenue', to: '/platform/revenue' },
      { label: 'Health & Monitoring', to: '/platform/health' },
    ],
  },
  {
    heading: 'Organizations',
    items: [
      { label: 'All Tenants', to: '/platform/tenants' },
      { label: 'Enroll Organization', to: '/platform/enroll' },
    ],
  },
  {
    heading: 'Infrastructure',
    items: [
      { label: 'Database', to: '/platform/settings/database' },
      { label: 'Redis', to: '/platform/settings/redis' },
      { label: 'Authentication', to: '/platform/settings/auth' },
      { label: 'Email', to: '/platform/settings/email' },
      { label: 'Storage', to: '/platform/settings/storage' },
    ],
  },
  {
    heading: 'Platform Operations',
    items: [
      { label: 'Webhooks (System)', to: '/platform/settings/webhooks' },
      { label: 'Audit Log', to: '/platform/audit-log' },
      { label: 'All Settings', to: '/platform/settings' },
    ],
  },
];

interface PlatformLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function PlatformLayout({ children, title }: PlatformLayoutProps): React.JSX.Element {
  const { user, logout } = useAdminAuth();
  const { isPlatformAdmin } = useOrgContext();
  const navigate = useNavigate();

  function handleLogout(): void {
    void logout();
    navigate('/login', { replace: true });
  }

  if (!isPlatformAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="rounded-xl border border-red-200 bg-white p-8 text-center shadow-lg">
          <h2 className="text-lg font-semibold text-red-700">Access Denied</h2>
          <p className="mt-2 text-sm text-slate-600">Platform admin permissions required.</p>
          <button
            onClick={() => navigate('/overview')}
            className="mt-4 rounded-lg bg-cyan-600 px-4 py-2 text-sm text-white hover:bg-cyan-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.16),_transparent_28%),linear-gradient(180deg,_#0c0617_0%,_#1e1033_48%,_#e2e8f0_48%,_#f8fafc_100%)] text-slate-900">
      <div className="flex min-h-screen">
        {/* Platform Sidebar */}
        <aside className="w-64 flex-shrink-0 border-r border-white/10 bg-slate-950/90 text-white backdrop-blur">
          <div className="border-b border-white/10 px-5 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-purple-300/80">Rule42 Billing</p>
            <span className="mt-2 block text-xl font-semibold text-white">Platform Admin</span>
            <p className="mt-2 text-sm text-slate-400">Manage tenants, infrastructure, and platform operations.</p>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-5">
            {PLATFORM_NAV_GROUPS.map((group, gi) => (
              <div key={group.heading} className={gi > 0 ? 'mt-5' : ''}>
                <p className="mb-2 px-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">{group.heading}</p>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `mb-1 block rounded-xl px-4 py-3 text-sm transition-colors ${
                        isActive
                          ? 'bg-purple-400/15 text-white shadow-[inset_0_0_0_1px_rgba(192,132,252,0.24)]'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <div className="border-t border-white/10 px-5 py-4 text-xs text-slate-400">
            <div className="rounded-xl bg-white/5 px-3 py-3">
              <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Platform Admin</p>
              <p className="mt-1 truncate text-sm text-slate-200">{user?.email ?? ''}</p>
            </div>
          </div>
        </aside>

        {/* Main area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="flex items-center justify-between border-b border-slate-200/70 bg-white/80 px-8 py-5 shadow-[0_18px_50px_-38px_rgba(15,23,42,0.75)] backdrop-blur">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-purple-700">Platform administration</p>
              <h1 className="mt-1 text-xl font-semibold text-slate-950">{title ?? 'Platform'}</h1>
            </div>
            <div className="flex items-center gap-3">
              <OrgSwitcher />
              <button
                onClick={handleLogout}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-950"
              >
                Logout
              </button>
            </div>
          </header>
          <main className="flex-1 overflow-y-auto px-8 py-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
